(() => {
  const initializedAttribute = 'data-customall-thumbnail-color-sync';
  const previewSelector = '#artwork-preview';
  const maximumFrameAttempts = 30;
  const previewWaitTimeout = 15000;
  const observedPreviews = new WeakSet();
  const scheduledPreviews = new WeakSet();

  if (document.documentElement.hasAttribute(initializedAttribute)) return;
  document.documentElement.setAttribute(initializedAttribute, '');

  const normalizeValue = (value) => String(value ?? '').trim().toLowerCase();

  const isCustomallPreview = (preview) =>
    preview?.id === 'artwork-preview' &&
    Boolean(preview.querySelector('.customall-product-media, .pack-canvas-host'));

  const getVariantColorMap = (scope) => {
    const script = scope.querySelector('[data-customall-variant-color-map]');
    if (!script) return new Map();

    try {
      const data = JSON.parse(script.textContent);
      return new Map(
        Object.entries(data.variants || {}).map(([variantId, color]) => [String(variantId), String(color)])
      );
    } catch (error) {
      console.warn('Unable to read the Customall variant color map.', error);
      return new Map();
    }
  };

  const getCustomallColorSelect = (scope) => {
    const optionContainer = Array.from(scope.querySelectorAll('[data-ctm-option-name]')).find(
      (element) => normalizeValue(element.getAttribute('data-ctm-option-name')) === 'color'
    );

    return optionContainer?.querySelector('select') || null;
  };

  const getMockupForIndex = (preview, index) =>
    Array.from(preview.querySelectorAll('.product-mockup[index]')).find(
      (mockup) => mockup.getAttribute('index') === index
    );

  const getActiveIndex = (preview) => {
    const activeMockup = preview.querySelector('.main-carousel .embla__slide.current-slide .product-mockup[index]');
    const activeMockupIndex = activeMockup?.getAttribute('index');
    if (activeMockupIndex !== null && activeMockupIndex !== undefined) return activeMockupIndex;

    const selectedThumbnail = preview.querySelector(
      '.thumb-carousel .embla__slide--thumb.is-selected img[data-index]'
    );
    return selectedThumbnail?.dataset.index !== undefined ? String(selectedThumbnail.dataset.index) : null;
  };

  const getUniqueColor = (mockup, variantColorMap) => {
    const variantIds = (mockup.getAttribute('variants') || '')
      .split(',')
      .map((variantId) => variantId.trim())
      .filter(Boolean);

    if (!variantIds.length) return null;

    const colors = new Set(
      variantIds.map((variantId) => variantColorMap.get(variantId)).filter((color) => Boolean(color))
    );

    return colors.size === 1 ? colors.values().next().value : null;
  };

  const updateCustomallColor = (preview, index, attempt = 0) => {
    if (!isCustomallPreview(preview)) return;

    const activeIndex = getActiveIndex(preview);
    if (activeIndex !== null && activeIndex !== index) return;

    const productInfo = preview.closest('product-info');
    if (!productInfo) return;

    const mockup = getMockupForIndex(preview, index);
    if (!mockup) {
      if (attempt < maximumFrameAttempts) {
        requestAnimationFrame(() => updateCustomallColor(preview, index, attempt + 1));
      }
      return;
    }

    const color = getUniqueColor(mockup, getVariantColorMap(productInfo));
    if (!color) return;

    const colorSelect = getCustomallColorSelect(productInfo);
    if (!colorSelect) return;

    const matchingOption = Array.from(colorSelect.options).find(
      (option) => normalizeValue(option.value) === normalizeValue(color)
    );

    if (!matchingOption || colorSelect.value === matchingOption.value) return;

    const valueSetter = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value')?.set;
    if (valueSetter) {
      valueSetter.call(colorSelect, matchingOption.value);
    } else {
      colorSelect.value = matchingOption.value;
    }

    colorSelect.dispatchEvent(new Event('input', { bubbles: true }));
    colorSelect.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const syncPreviewColor = (preview) => {
    const activeIndex = getActiveIndex(preview);
    if (activeIndex === null) return;
    updateCustomallColor(preview, activeIndex);
  };

  const schedulePreviewSync = (preview) => {
    if (!isCustomallPreview(preview) || scheduledPreviews.has(preview)) return;

    scheduledPreviews.add(preview);
    requestAnimationFrame(() => {
      scheduledPreviews.delete(preview);
      syncPreviewColor(preview);
    });
  };

  const observePreview = (preview) => {
    if (!isCustomallPreview(preview) || observedPreviews.has(preview)) return false;

    observedPreviews.add(preview);
    const previewObserver = new MutationObserver(() => schedulePreviewSync(preview));
    previewObserver.observe(preview, {
      attributes: true,
      attributeFilter: ['class'],
      childList: true,
      subtree: true,
    });
    schedulePreviewSync(preview);
    return true;
  };

  document.querySelectorAll('product-info').forEach((productInfo) => {
    const preview = productInfo.querySelector(previewSelector);
    if (observePreview(preview)) return;

    const productObserver = new MutationObserver(() => {
      const injectedPreview = productInfo.querySelector(previewSelector);
      if (!observePreview(injectedPreview)) return;

      productObserver.disconnect();
      window.clearTimeout(disconnectTimeout);
    });

    productObserver.observe(productInfo, { childList: true, subtree: true });
    const disconnectTimeout = window.setTimeout(() => productObserver.disconnect(), previewWaitTimeout);
  });
})();
